/* eslint-disable @typescript-eslint/no-explicit-any */

import { devtoolsExchange } from "@urql/devtools";
import { cacheExchange, CacheExchangeOpts } from "@urql/exchange-graphcache";
import { useAccessToken } from "auth/utils/useAccessToken";
import React, { FC, useMemo } from "react";
import { Client, createClient, Exchange, fetchExchange, Provider } from "urql";

import { appUpdates } from "./app";
import { faqKeys, faqUpdates } from "./faq";
import { entityUpdates } from "./entity";
import { handlerUpdates } from "./handler";
import { intentUpdates } from "./intent";
import { orgUpdates } from "./org";

import schema from "generated/schema.json";

export interface URQLProviderProps {
    api?: string;
    children: JSX.Element | JSX.Element[];
}

function createUrqlClient(api: string, accessToken: string): Client {
    /*
     * The GrapheCache is a schema aware cache that provides more features than then
     * built in document cache based cacheExchange.
     *
     * We configure the parameters the keys are derived from, optimistic returns, and
     * updates to the cache based off of create and delete operations.
     */
    const options: CacheExchangeOpts = {
        keys: {
            ActionsOnGoogleAppChannel: (data: any): any => data.id,
            AlexaAppChannel: (data: any): any => data.id,
            App: (data: any) => data.appId,
            AppLimitations: (data: any): any => null,
            AppAnalytics: (data: any): any => null,
            AppUsageStat: (data: any): any => null,
            AppPlaceDescription: (data: any): any => data.placeId,
            AppPlaceDescriptionAddressComponent: (data: any): any => null,
            BaseAppChannel: (data: any) => data.id,
            Lead: (data: any): any => data.id,
            LeadData: (data: any): any => null,
            LeadsQuery: (data: any): any => null,
            ChatWidgetAppChannel: (data: any) => data.id,
            ChatWidgetBorderTheme: (data: any): any => null,
            ChatWidgetBrandingTheme: (data: any): any => null,
            ChatWidgetButtonTheme: (data: any): any => null,
            ChatWidgetCarouselTheme: (data: any): any => null,
            ChatWidgetChatButtonTheme: (data: any): any => null,
            ChatWidgetContentTheme: (data: any): any => null,
            ChatWidgetCtaTheme: (data: any): any => null,
            ChatWidgetFooterConfig: (data: any): any => null,
            ChatWidgetFooterTheme: (data: any): any => null,
            ChatWidgetHeaderActionsConfig: (data: any): any => null,
            ChatWidgetHeaderConfig: (data: any): any => null,
            ChatWidgetHeaderTheme: (data: any): any => null,
            ChatWidgetMenuTheme: (data: any): any => null,
            ChatWidgetInputConfig: (data: any): any => null,
            ChatWidgetInputTheme: (data: any): any => null,
            ChatWidgetMenuItemTheme: (data: any): any => null,
            ChatWidgetMessageTheme: (data: any): any => null,
            ChatWidgetPaddingTheme: (data: any): any => null,
            ChatWidgetServerConfig: (data: any): any => null,
            ChatWidgetSizeTheme: (data: any): any => null,
            ChatWidgetTextTheme: (data: any): any => null,
            ChatWidgetTextTypingStatusTheme: (data: any): any => null,
            ChatWidgetTheme: (data: any): any => null,
            CRMServiceAvailabilityBusyDayDescription: (data: any): any => null,
            CRMServiceAvailabilitySettings: (data: any): any => null,
            CtaConfig: (data: any): any => null,
            DialogflowAppChannel: (data: any): any => data.id,
            Entity: (data: any) => data.entityId,
            EntityValue: (data: any) => data.name,
            EntityValidation: (data: any) => data.null,
            EntitiesQuery: (data: any) => data.null,
            EventFlag: (data: any): any => null,
            Events: (data: any) => data.eventId,
            EventResponse: (data: any) => null,
            EventResponseObject: (data: any) => null,
            EventSlots: (data: any) => null,
            EventStentorRequest: (data: any) => null,
            FlagTotals: (data: any): any => null,
            FormWidgetStandAloneTheme: (data: any): any => null,
            FormWidgetConnectionConfig: (data: any): any => null,
            FormWidgetTheme: (data: any): any => null,
            FormWidgetCheckboxTheme: (data: any): any => null,
            FormWidgetChipsTheme: (data: any): any => null,
            FormWidgetCardTheme: (data: any): any => null,
            FormWidgetDateTheme: (data: any): any => null,
            FormWidgetDropdownTheme: (data: any): any => null,
            FormWidgetSideButtonTheme: (data: any): any => null,
            FormWidgetTextTheme: (data: any): any => null,
            FormWidgetAutoOpen: (data: any): any => null,
            GetAppsListApp: (data: any) => `${data.organizationId}:${data.appId}`,
            // This one below is soo incredibly important and I don't know why
            // It causes weird caching behavior if it is set to _id
            GetAppsQuery: (data: any) => null,
            GetAppsListStatus: (data: any) => null,
            GetEntitiesListEntity: (data: any) => data.entityId,
            GetHandlersListHandler: (data: any) => data.intentId,
            GetIntentsListIntent: (data: any) => data.intentId,
            GraphConnectionKeyDescription: (data: any) => data.intentId,
            GraphConnections: (data: any) => data.id,
            GraphNode: (data: any) => data.id,
            Handler: (data: any) => data.intentId,
            HandlerContent: (data: any): any => null,
            HandlerResponse: (data: any): any => null,
            HandlerValidation: (data: any) => null,
            HandlerValidationError: (data: any): any => null,
            HandlersQuery: (data: any): any => null,
            Integration: (data: any) => data.integrationId,
            IntelligentSearchAppChannel: (data: any) => data.id,
            IntelligentSearchConnectionConfig: (data: any) => null,
            IntelligentSearchWidgetPaddingTheme: (data: any) => null,
            IntelligentSearchWidgetTextBlockTheme: (data: any) => null,
            IntelligentSearchWidgetTextTheme: (data: any) => null,
            IntelligentSearchWidgetTheme: (data: any) => null,
            IntelligentWidgetBorderTheme: (data: any) => null,
            IntelligentSearchWidgetMarginTheme: (data: any) => null,
            IntelligentSearchWidgetMessagesTheme: (data: any) => null,
            IntelligentSearchWidgetMessageTheme: (data: any) => null,
            IntelligentSearchWidgetBubbleTheme: (data: any) => null,
            Intent: (data: any) => data.intentId,
            IntentValidation: (data: any) => null,
            IntentValidationError: (data: any) => null,
            IntentsQuery: (data: any) => null,
            IPrights: (data: any): any => null,
            LeadField: (data: any): any => null,
            LeadSentToIntegration: (data: any): any => null,
            LeadTranscript: (data: any): any => null,
            LeadTranscriptUserProfile: (data: any): any => null,
            LexConnectAppChannel: (data: any) => data.id,
            LexV2ConnectAppChannel: (data: any) => data.id,
            LinkOutSuggestion: (data: any) => null,
            ListDisplay: (data: any) => null,
            ListDisplayItem: (data: any) => null,
            ListDisplayRange: (data: any) => null,
            MobileCtaConfig: (data: any) => null,
            OnboardingQuery: (data: any) => null,
            OrgAnalytics: (data: any): any => null,
            OrgAcceptedTermsOfService: (data: any): any => null,
            Organization: (data: any) => data.organizationId,
            OrgsQuery: (data: any): any => null,
            OrgLimitations: (data: any): any => null,
            OrgUsageStat: (data: any) => data.organizationId,
            OrgUsageStats: (data: any): any => null,
            PartialOrgLimitations: (data: any): any => null,
            PaymentAccounts: (data: any) => data._id,
            ResponseOutput: (data: any): any => null,
            SearchedOrg: (data: any) => data.organizationId,
            SimpleHandlerResponse: (data: any) => null,
            Status: (data: any): any => null,
            StatusHistory: (data: any): any => null,
            StripePaymentAccount: (data: any): any => null,
            SubscriptionDetails: (data: any): any => null,
            Slot: (data: any): any => null,
            SuggestionObject: (data: any): any => null,
            TotalEvents: (data: any): any => null,
            TotalWebContent: (data: any): any => null,
            TotalWebContentSources: (data: any): any => null,
            TotalWebFAQ: (data: any): any => null,
            UsageStat: (data: any): any => null,
            //       UserAnalytics: (data: any): any => null,
            UserEntitlements: (data: any): any => null,
            UserProfileRole: (data: any): any => null,
            WebContent: (data: any) => data._id,
            WebContentSources: (data: any): any => null,
            WebDetailsExecution: (data: any): any => null,
            WebsiteData: (data: any): any => null,
            WebsiteDataBusinessData: (data: any): any => null,
            WebsiteDataCallsToAction: (data: any): any => null,
            WebsiteDataDescriptiveData: (data: any): any => null,
            WebsiteDataDetectedTechnology: (data: any): any => null,
            WidgetConfigurableMessagesConfig: (data: any): any => null,
            ...faqKeys,
        },
        updates: {
            Mutation: {
                ...entityUpdates,
                ...faqUpdates,
                ...intentUpdates,
                ...handlerUpdates,
                ...orgUpdates,
                ...appUpdates,
            },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore The types are correct
        schema,
    };

    const exchanges: Exchange[] = [];

    if (__ENV__ === "development") {
        // If on dev, add this first for debugging with the chrome extension
        exchanges.push(devtoolsExchange);
    }
    // Push the defaults, order is important here
    // TODO: Figure out how to strongly type the cacheExchange with our updates
    exchanges.push(cacheExchange(options) as unknown as Exchange, fetchExchange);

    const client = createClient({
        exchanges,
        fetchOptions: {
            headers: {
                "content-type": "application/json",
                "x-xapp-usertoken": accessToken || undefined,
            },
        },
        url: `${api}/graphql`,
    });

    return client;
}

export const URQLProvider: FC<URQLProviderProps> = (props) => {
    const { api } = props;

    const accessToken = useAccessToken();

    const client = useMemo(() => {
        return createUrqlClient(api, accessToken);
    }, [api, accessToken]);

    return <Provider value={client}>{props.children}</Provider>;
};
